import React, { useState, useEffect } from 'react';
import { createService, updateService, getCategories } from '../../services/api';
import { FormInput } from '../components/FormInput';
import { useImageUpload } from '../hooks/useImageUpload';
import { textToArray, arrayToText, getFullImageUrl } from '../utils/helpers';
import toast from 'react-hot-toast';

const ServiceForm = ({ editingItem, onClose, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    category: editingItem?.category || '',
    icon: editingItem?.icon || 'fas fa-cogs',
    description: editingItem?.description || '',
    price: editingItem?.price || '',
    features: arrayToText(editingItem?.features || []),
    imageUrl: editingItem?.imageUrl || '',
    order: editingItem?.order || 0,
    isActive: editingItem?.isActive !== false
  });
  
  const [loading, setLoading] = useState(false);
  const { imageFile, uploading, preview, handleImageChange, uploadImage } = useImageUpload();

  const API_BASE = process.env.REACT_APP_API_URL || 'https://inteliheads.in/api';

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      
      // Handle response structure and filter only service categories
      if (response && response.data && Array.isArray(response.data)) {
        categoriesArray = response.data.filter(cat => cat.type === 'service');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'service');
      } else if (response && response.categories && Array.isArray(response.categories)) {
        categoriesArray = response.categories.filter(cat => cat.type === 'service');
      }
      
      setCategories(categoriesArray);
      console.log('Service categories loaded:', categoriesArray.length);
      
      // If editing and has category, set it
      if (editingItem?.category && categoriesArray.length > 0 && !formData.category) {
        const existingCategory = categoriesArray.find(c => c.name === editingItem.category);
        if (existingCategory) {
          setFormData(prev => ({ ...prev, category: existingCategory.name }));
        }
      }
      
      // Set default category if available and none selected
      if (!formData.category && categoriesArray.length > 0) {
        setFormData(prev => ({ ...prev, category: categoriesArray[0].name }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories if API fails
      setCategories([
        { _id: '1', name: 'Consulting', type: 'service', icon: '💼' },
        { _id: '2', name: 'Development', type: 'service', icon: '⚙️' },
        { _id: '3', name: 'Support', type: 'service', icon: '🎧' },
        { _id: '4', name: 'Training', type: 'service', icon: '🎓' }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          name: newCategory.trim(),
          type: 'service',
          icon: '⚙️',
          order: categories.length + 1,
          isActive: true
        })
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success('Category added successfully');
        const newCat = { _id: result.data._id, name: newCategory.trim(), type: 'service', icon: '⚙️' };
        setCategories([...categories, newCat]);
        setFormData({ ...formData, category: newCategory.trim() });
        setNewCategory('');
        setShowNewCategoryInput(false);
      } else {
        toast.error(result.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Error adding category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    setLoading(true);
    let imageUrl = formData.imageUrl;
    if (imageFile) imageUrl = await uploadImage();
    
    const submitData = {
      name: formData.name,
      category: formData.category,
      icon: formData.icon,
      description: formData.description,
      price: formData.price,
      features: textToArray(formData.features),
      imageUrl: imageUrl,
      order: parseInt(formData.order),
      isActive: formData.isActive
    };
    
    try {
      if (editingItem && editingItem._id) {
        await updateService(editingItem._id, submitData);
        toast.success('Service updated successfully');
      } else {
        await createService(submitData);
        toast.success('Service created successfully');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error(error.message || 'Error saving service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '650px', width: '90%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>{editingItem ? 'Edit Service' : 'Add New Service'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <FormInput label="Service Name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          
          {/* Category Selection - Only Service Categories */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Category *</label>
            {loadingCategories ? (
              <div style={{ padding: '0.75rem', color: '#5A6E7F' }}>
                <i className="fas fa-spinner fa-spin"></i> Loading categories...
              </div>
            ) : (
              <>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
                  required
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.5rem' }}
                >
                  <option value="">-- Select a category --</option>
                  {categories.map((cat) => (
                    <option key={cat._id || cat.name} value={cat.name}>
                      {cat.icon && <span style={{ marginRight: '0.5rem' }}>{cat.icon}</span>}
                      {cat.name}
                    </option>
                  ))}
                </select>
                
                {!showNewCategoryInput ? (
                  <button 
                    type="button"
                    onClick={() => setShowNewCategoryInput(true)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      color: '#2C7A7B',
                      border: '1px solid #2C7A7B',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    <i className="fas fa-plus"></i> Add New Category
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Enter new category name"
                      style={{ flex: 1, padding: '0.5rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      style={{ padding: '0.5rem 1rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory('');
                      }}
                      style={{ padding: '0.5rem 1rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <FormInput label="Icon Class" name="icon" value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="fas fa-cogs" />
            <FormInput label="Price" name="price" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="Contact for pricing" />
          </div>
          
          <FormInput label="Description" name="description" type="textarea" rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <FormInput label="Features (one per line)" name="features" type="textarea" rows="4" value={formData.features} onChange={(e) => setFormData({ ...formData, features: e.target.value })} />
          <FormInput label="Order" name="order" type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: e.target.value })} />
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Service Image</label>
            {preview && <img src={preview} alt="Preview" style={{ width: '80px', marginBottom: '0.5rem', display: 'block', borderRadius: '8px' }} />}
            {formData.imageUrl && !preview && <img src={getFullImageUrl(formData.imageUrl)} alt="Current" style={{ width: '80px', marginBottom: '0.5rem', display: 'block', borderRadius: '8px' }} />}
            <input type="file" accept="image/*" onChange={(e) => handleImageChange(e.target.files[0])} style={{ width: '100%', marginBottom: '0.5rem', padding: '0.5rem' }} />
            <input 
              type="text" 
              value={formData.imageUrl} 
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} 
              placeholder="Image URL" 
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '0.5rem' }} 
            />
          </div>
          
          <FormInput label="Active" name="isActive" type="checkbox" value={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
          
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" disabled={loading || uploading} style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              {loading || uploading ? 'Saving...' : 'Save Service'}
            </button>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm;